/**
 * Input field formatters for automatic standardization.
 * Used by useFormFormatter hook to auto-format fields as the user types.
 */

import { formatRut } from './rutValidator'

/**
 * Formats a RUT input as the user types.
 * Removes invalid characters, applies dots and dash formatting.
 * Example: "123456789" -> "12.345.678-9"
 */
export function formatRutInput(value: string): string {
  // Strip everything except digits and K/k
  const cleaned = value.replace(/[^0-9kK]/g, '').toUpperCase()
  if (cleaned.length === 0) return ''
  if (cleaned.length < 2) return cleaned

  return formatRut(cleaned)
}

/**
 * Formats a name field: capitalizes the first letter of each word, trims extra spaces.
 * Example: "  juan   PEDRO " -> "Juan Pedro"
 */
export function formatNameInput(value: string): string {
  return value
    .replace(/\s+/g, ' ')
    .replace(/^\s/, '')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\B\w+/g, (word) => word.toLowerCase())
}

/**
 * Formats an email field: trims spaces, converts to lowercase.
 * Example: "  Juan.Perez@Gmail.COM " -> "juan.perez@gmail.com"
 */
export function formatEmailInput(value: string): string {
  return value.replace(/\s/g, '').toLowerCase()
}

/**
 * Formats a phone number field on blur.
 * Strips invalid characters (keeps digits, +, spaces, dashes, parentheses),
 * collapses multiple spaces, and trims.
 * Does NOT assume a specific country format.
 *
 * Example: "  +56  9  1234 5678  " -> "+56 9 1234 5678"
 * Example: "+1 (555) 123-4567" -> "+1 (555) 123-4567"
 * Example: "abc912345678xyz" -> "912345678"
 */
export function formatPhoneInput(value: string): string {
  return value
    .replace(/[^\d+\s()-]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Formats a field that accepts either email or RUT (login field).
 * Detects which type based on input content: if contains '@' or alpha chars -> email, else -> RUT.
 * Example (email): "  Juan@GMAIL.com " -> "juan@gmail.com"
 * Example (rut): "123456789" -> "12.345.678-9"
 */
export function formatEmailOrRutInput(value: string): string {
  const trimmed = value.trim()
  if (trimmed.length === 0) return ''

  // If contains '@' or starts with a letter, treat as email
  if (trimmed.includes('@') || /^[a-zA-Z]/.test(trimmed)) {
    return formatEmailInput(value)
  }

  // Otherwise treat as RUT
  return formatRutInput(value)
}

/** Map of field type to formatter function */
export const fieldFormatters = {
  rut: formatRutInput,
  name: formatNameInput,
  email: formatEmailInput,
  phone: formatPhoneInput,
  emailOrRut: formatEmailOrRutInput,
} as const

export type FieldFormatterType = keyof typeof fieldFormatters
